const STORAGE_KEY = "apit:tenant-config";

export function getTenantConfig() {
	if (typeof window === "undefined") return null;
	try {
		const value = window.localStorage.getItem(STORAGE_KEY);
		return value ? JSON.parse(value) : null;
	} catch (_) {
		return null;
	}
}

export function setTenantConfig(config) {
	if (typeof window === "undefined" || !config?.key) return;
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
	} catch (_) {
		// Authentication must still work when localStorage is unavailable.
	}
}

function normalizedModuleShape(config) {
	return {
		key: config?.key || null,
		enabledModules: Array.isArray(config?.enabledModules)
			? [...config.enabledModules].map((m) => String(m).trim().toLowerCase()).sort()
			: [],
		disabledPages: Array.isArray(config?.disabledPages)
			? [...config.disabledPages].map((p) => String(p).trim()).sort()
			: [],
		moduleEnabledPages: config?.moduleEnabledPages && typeof config.moduleEnabledPages === "object"
			? config.moduleEnabledPages
			: {},
	};
}

/**
 * `config.js` computes its menu once from localStorage at script-load time (see
 * ananunadm/src/config.js filteredMenu). A login/verify response can change the
 * cached tenant config *after* that computation already ran, leaving the menu
 * permanently stale for the rest of the page's lifetime. Callers use this to
 * detect that case and force a reload so config.js re-evaluates with fresh data.
 */
export function tenantModuleShapeChanged(nextConfig) {
	const prev = normalizedModuleShape(getTenantConfig());
	const next = normalizedModuleShape(nextConfig);
	return JSON.stringify(prev) !== JSON.stringify(next);
}

export function getRuntimeEnabledModules(fallback = ["all"]) {
	const configured = getTenantConfig()?.enabledModules;
	if (!Array.isArray(configured) || configured.length === 0) return fallback;
	return configured
		.map((moduleName) => String(moduleName).trim().toLowerCase())
		.filter(Boolean)
		.map((moduleName) => moduleName === "meeting" ? "meetings" : moduleName);
}

export function getRuntimeDisabledPages(fallback = []) {
	const configured = getTenantConfig()?.disabledPages;
	if (!Array.isArray(configured)) return fallback;
	return configured.map((page) => String(page).trim()).filter(Boolean);
}

export function getRuntimeModuleEnabledPages(moduleName, fallback = null) {
	const configured = getTenantConfig()?.moduleEnabledPages;
	if (!configured || typeof configured !== "object") return fallback;
	if (!Object.prototype.hasOwnProperty.call(configured, moduleName)) return fallback;
	const pages = configured[moduleName];
	if (!Array.isArray(pages)) return fallback;
	return pages.map((page) => String(page).trim()).filter(Boolean);
}
